import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert, Badge, Accordion } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import IconButton from 'components/common/IconButton';
import ModalConfirmacionPagoSuscripcion from './ModalConfirmacionPagoSuscripcion';

/**
 * ModalNuevoUsuarioAkasiaPOS
 * Flujo simplificado de add-user para licencias con suscripcion activa.
 */
const ModalNuevoUsuarioAkasiaPOS = ({ show, onHide, idLicencia: initialIdLicencia, licencias, onCreated, infoSuscripcionProp }) => {
    const [saving, setSaving] = useState(false);
    const [loadingContext, setLoadingContext] = useState(false);
    const [createUserContext, setCreateUserContext] = useState(null);
    const [contextError, setContextError] = useState('');
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
    const [payloadPendiente, setPayloadPendiente] = useState(null);
    const [values, setValues] = useState({
        vUsuario: '',
        vContrasena: '',
        vEmail: ''
    });

    const { token, setToken } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const selectedLicense = Number(initialIdLicencia || licencias?.idLicencia || 0);
    const selectedBranch = branches.find(branch => String(branch.idSucursal) === String(selectedBranchId));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const generarCadenaRandom = async (campo) => {
        try {
            const response = await fetch('/api/licencia/ObtenerCadenaRandom', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'text/plain'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                    return;
                }

                showErrorToast('Error al generar cadena aleatoria');
                return;
            }

            const data = await response.text();
            setValues(prev => ({
                ...prev,
                [campo]: data
            }));
        } catch (error) {
            showErrorToast('Error al generar cadena: ' + error.message);
        }
    };

    const cargarContextoAltaUsuario = async (idLicenciaToUse) => {
        if (!idLicenciaToUse) {
            setCreateUserContext(null);
            setContextError('No se recibió una licencia válida.');
            return;
        }

        setLoadingContext(true);
        setContextError('');

        try {
            const response = await fetch(`/api/UsuariosAkasiaPOS/ObtenerContextoAltaUsuario?idLicencia=${idLicenciaToUse}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                    return;
                }

                setCreateUserContext(null);
                setContextError(`${response.status} ${response.statusText}`);
                return;
            }

            const data = await response.json();
            if (data.iResponseType === 1 && data.detalles) {
                setCreateUserContext(JSON.parse(data.detalles));
                return;
            }

            setCreateUserContext(null);
            setContextError(data.vMessage || 'La licencia no tiene una suscripción activa para agregar usuarios.');
        } catch (error) {
            setCreateUserContext(null);
            setContextError(`Error al obtener el contexto de suscripción para crear el usuario: ${error.message}`);
        } finally {
            setLoadingContext(false);
        }
    };

    const cargarSucursales = async (idLicenciaToUse, companyUuid) => {
        if (!idLicenciaToUse || !companyUuid) {
            setBranches([]);
            setSelectedBranchId('');
            return;
        }

        setLoadingBranches(true);
        try {
            const response = await fetch(`/api/UsuariosAkasiaPOS/ObtenerSucursalesEmpresa?vUUIDEmpresa=${encodeURIComponent(companyUuid)}&idLicencia=${idLicenciaToUse}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                    return;
                }

                setBranches([]);
                setSelectedBranchId('');
                return;
            }

            const data = await response.json();
            if (data.iResponseType === 1 && data.detalles) {
                const parsedBranches = JSON.parse(data.detalles);
                if (Array.isArray(parsedBranches) && parsedBranches.length > 0) {
                    setBranches(parsedBranches);
                    setSelectedBranchId(currentValue => currentValue || String(parsedBranches[0].idSucursal));
                    return;
                }
            }

            setBranches([]);
            setSelectedBranchId('');
        } catch {
            setBranches([]);
            setSelectedBranchId('');
        } finally {
            setLoadingBranches(false);
        }
    };

    useEffect(() => {
        if (show) {
            cargarContextoAltaUsuario(selectedLicense);
            cargarSucursales(selectedLicense, licencias?.vUUIDEmpresa);
            return;
        }

        setValues({ vUsuario: '', vContrasena: '', vEmail: '' });
        setCreateUserContext(null);
        setContextError('');
        setBranches([]);
        setSelectedBranchId('');
        setPayloadPendiente(null);
        setShowModalConfirmacion(false);
    }, [show, selectedLicense, token, licencias?.vUUIDEmpresa]);

    const handleSave = async () => {
        if (!selectedLicense) return showErrorToast('No se encontró la licencia a procesar.');
        if (!createUserContext) return showErrorToast(contextError || 'La licencia no tiene un contexto válido para add-user.');
        if (!selectedBranchId) return showErrorToast('Selecciona una sucursal para el usuario.');
        if (!values.vUsuario) return showErrorToast('Usuario es obligatorio');
        if (!values.vEmail) return showErrorToast('Correo es obligatorio');
        if (!values.vContrasena) return showErrorToast('Contraseña es obligatoria');
        if (/\s/.test(values.vContrasena)) return showErrorToast('La contraseña no debe contener espacios');
        if (/\s/.test(values.vEmail)) return showErrorToast('El correo no debe contener espacios');

        const emailRe = /^\S+@\S+\.\S+$/;
        if (!emailRe.test(values.vEmail)) return showErrorToast('Correo inválido. Formato mínimo: x@x.x');

        const payload = {
            idLicencia: selectedLicense,
            idSucursal: Number(selectedBranchId),
            vUsername: values.vUsuario,
            vEmail: values.vEmail,
            vPassword: values.vContrasena,
            vUUIDEmpresa: licencias?.vUUIDEmpresa || '',
            dCobroDiferencial: createUserContext.dCobroDiferencial,
            vMoneda: createUserContext.vMoneda,
            vTipoRenovacion: createUserContext.vTipoRenovacion,
            idProductoPrecio: createUserContext.idProductoPrecio,
            idProductoRenovaciones: createUserContext.idProductoRenovaciones,
            idSuscripcion: createUserContext.idSuscripcion
        };

        if (createUserContext.bRequierePago && Number(createUserContext.dCobroDiferencial || 0) > 0) {
            setPayloadPendiente(payload);
            setShowModalConfirmacion(true);
            return;
        }

        await ejecutarCreacionUsuario(payload, false);
    };

    const ejecutarCreacionUsuario = async (payload, cobrarAhora = false) => {
        setSaving(true);
        try {
            const payloadFinal = { ...payload, bCobrarAhora: cobrarAhora };

            const res = await fetch('/api/UsuariosAkasiaPOS/CrearUsuarioConSuscripcion', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(JSON.stringify(payloadFinal))
            });

            if (!res.ok) {
                const errorText = await res.text();
                showErrorToast(`Error ${res.status}: ${errorText || res.statusText}`);
                return;
            }

            const data = await res.json();
            if (data?.iResponseType === 1) {
                showSuccessToast(data.vMessage || 'Usuario POS creado correctamente.');
                onCreated?.();
                onHide();
                return;
            }

            showErrorToast(data?.vMessage || data?.vCode || 'No se pudo crear el usuario');
        } catch (err) {
            showErrorToast('Error al crear usuario: ' + err.message);
        } finally {
            setSaving(false);
            setPayloadPendiente(null);
        }
    };

    const handleConfirmacionPago = async (cobrarAhora) => {
        setShowModalConfirmacion(false);
        if (payloadPendiente) {
            await ejecutarCreacionUsuario(payloadPendiente, cobrarAhora);
        }
    };

    const renderSubscriptionSummary = () => {
        if (loadingContext) {
            return (
                <Alert variant="light" className="mb-0 d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Preparando contexto de suscripción...
                </Alert>
            );
        }

        if (contextError) {
            return (
                <Alert variant="warning" className="mb-0">
                    <div className="fw-bold mb-1">No se pudo preparar add-user</div>
                    <div>{contextError}</div>
                </Alert>
            );
        }

        if (!createUserContext) {
            return (
                <div className="text-muted small">
                    No hay contexto disponible para mostrar información comercial de la suscripción.
                </div>
            );
        }

        return (
            <div className="d-flex flex-column gap-3">
                <div className="rounded-4 p-3 bg-100 dark__bg-1000 border">
                    <div>
                        <div className="fw-bold text-primary mb-2">Suscripción activa #{createUserContext.idSuscripcion}</div>
                        <div className="small text-uppercase text-700 fw-bold mb-1">Producto</div>
                        <div className="mb-3 text-900 dark__text-100">{createUserContext.vNombreProducto || 'Usuario Akasia POS'}</div>
                    </div>

                    <div className="small text-uppercase text-700 fw-bold mb-1">Rol asignado</div>
                    <div className="mb-3 text-900 dark__text-100">Administrador</div>

                    <div className="small text-uppercase text-700 fw-bold mb-1">Sucursal</div>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Badge bg="light" text="dark" pill className="border">
                            {selectedBranch?.vNombreSucursal || 'Selecciona una sucursal'}
                        </Badge>
                    </div>

                    <div className="small text-uppercase text-700 fw-bold mb-1">Renovación</div>
                    <div className="mb-3 text-900 dark__text-100">{createUserContext.vTipoRenovacion || infoSuscripcionProp?.vTipoRenovacion || '—'}</div>

                    <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                        <Badge bg={createUserContext.bRequierePago ? 'warning' : 'success'}>
                            {createUserContext.bRequierePago ? 'Cobro diferencial' : 'Sin cobro inmediato'}
                        </Badge>
                        <div className="fw-bold text-900 dark__text-100">
                            ${Number(createUserContext.dCobroDiferencial || 0).toFixed(2)} {createUserContext.vMoneda || infoSuscripcionProp?.vMoneda || 'MXN'}
                        </div>
                    </div>
                </div>

                <div className="rounded-4 border bg-white dark__bg-1100 p-3">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle bg-100 dark__bg-1000"
                            style={{ width: '48px', height: '48px' }}
                        >
                            <img src="/img/usuario20.png" alt="Usuario POS" style={{ width: '24px', height: '24px' }} />
                        </div>
                        <div>
                            <div className="fw-semibold text-900 dark__text-100">Perfil administrativo</div>
                            <div className="small text-700">El usuario quedará ligado a la licencia {selectedLicense} y operará en la sucursal seleccionada.</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDesktopSidebar = () => (
        <div className="d-none d-md-flex h-100 flex-column bg-100 dark__bg-1100 border-end p-3 p-xl-4">
            <div className="mb-3">
                <div className="fw-bold text-900 dark__text-100" style={{ fontSize: '1.9rem', lineHeight: 1.05 }}>
                    Nueva alta
                    <br />
                    Akasia POS
                </div>
                <div className="small text-700 mt-3">
                    Captura correo, usuario, contraseña y sucursal. El contexto comercial se toma de la suscripción activa.
                </div>
            </div>
            {renderSubscriptionSummary()}
        </div>
    );

    const renderMobileSummary = () => (
        <Accordion className="d-lg-none mt-3">
            <Accordion.Item eventKey="0" className="border rounded-4 overflow-hidden bg-white dark__bg-1100">
                <Accordion.Header>
                    <div className="d-flex flex-column text-start">
                        <span className="fw-semibold">Ver contexto de la suscripción</span>
                        <span className="small text-700">Producto, renovación, sucursal y cobro</span>
                    </div>
                </Accordion.Header>
                <Accordion.Body>
                    {renderSubscriptionSummary()}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                size="lg"
                backdrop="static"
                centered
                contentClassName="rounded-4 border-0 shadow-lg overflow-hidden bg-white dark__bg-1000"
            >
                <Modal.Body className="p-0">
                    <div className="overflow-hidden rounded-4">
                        <Row className="g-0 align-items-stretch">
                            <Col md={5} xl={5}>
                                {renderDesktopSidebar()}
                            </Col>

                            <Col md={7} xl={7}>
                                <div className="p-3 p-lg-4 bg-white dark__bg-1000 h-100 d-flex flex-column">
                                    <div className="mb-4 d-flex justify-content-between align-items-start gap-3">
                                        <div>
                                            <div className="fw-bold text-900 dark__text-100" style={{ fontSize: '1.9rem', lineHeight: 1.05 }}>Datos del nuevo usuario</div>
                                            <div className="small text-700 mt-1">
                                                Licencia: {selectedLicense || 'Sin licencia seleccionada'}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {selectedLicense > 0 && <Badge pill bg="primary" className="px-3 py-2">ID {selectedLicense}</Badge>}
                                            <button
                                                type="button"
                                                onClick={onHide}
                                                disabled={saving || loadingContext}
                                                className="btn btn-link text-700 text-decoration-none p-0 fs-2 lh-1"
                                                aria-label="Cerrar"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column gap-3">
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold text-700 mb-2 text-uppercase">
                                                Correo <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                name="vEmail"
                                                type="email"
                                                value={values.vEmail}
                                                onChange={handleChange}
                                                placeholder="correo@ejemplo.com"
                                                disabled={saving || loadingContext}
                                                className="py-2 bg-white dark__bg-1000"
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <Form.Label className="small fw-semibold text-700 mb-2 text-uppercase">
                                                Sucursal <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedBranchId}
                                                onChange={(event) => setSelectedBranchId(event.target.value)}
                                                disabled={saving || loadingContext || loadingBranches || branches.length === 0}
                                                className="py-2 bg-white dark__bg-1000"
                                            >
                                                {loadingBranches && <option value="">Cargando sucursales...</option>}
                                                {!loadingBranches && branches.length === 0 && <option value="">No hay sucursales disponibles</option>}
                                                {!loadingBranches && branches.length > 0 && branches.map(branch => (
                                                    <option key={branch.idSucursal} value={branch.idSucursal}>
                                                        {branch.vNombreSucursal}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                                <Form.Label className="small fw-semibold text-700 mb-0 text-uppercase">
                                                    Usuario <span className="text-danger">*</span>
                                                </Form.Label>
                                                <IconButton
                                                    iconClassName="fs--2 me-1"
                                                    variant="falcon-primary"
                                                    size="sm"
                                                    icon="circle"
                                                    onClick={() => generarCadenaRandom('vUsuario')}
                                                    disabled={saving || loadingContext}
                                                >
                                                    Generar
                                                </IconButton>
                                            </div>
                                            <Form.Control
                                                name="vUsuario"
                                                value={values.vUsuario}
                                                onChange={handleChange}
                                                placeholder="usuario_pos_01"
                                                disabled={saving || loadingContext}
                                                autoComplete="off"
                                                className="py-2 bg-white dark__bg-1000"
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                                <Form.Label className="small fw-semibold text-700 mb-0 text-uppercase">
                                                    Contraseña <span className="text-danger">*</span>
                                                </Form.Label>
                                                <IconButton
                                                    iconClassName="fs--2 me-1"
                                                    variant="falcon-primary"
                                                    size="sm"
                                                    icon="circle"
                                                    onClick={() => generarCadenaRandom('vContrasena')}
                                                    disabled={saving || loadingContext}
                                                >
                                                    Generar
                                                </IconButton>
                                            </div>
                                            <Form.Control
                                                name="vContrasena"
                                                type="text"
                                                value={values.vContrasena}
                                                onChange={handleChange}
                                                placeholder="Contraseña temporal"
                                                disabled={saving || loadingContext}
                                                autoComplete="new-password"
                                                className="py-2 bg-white dark__bg-1000"
                                            />
                                            <Form.Text className="text-700">
                                                La contraseña no puede contener espacios.
                                            </Form.Text>
                                        </Form.Group>
                                    </div>

                                    {renderMobileSummary()}

                                    <div className="d-flex justify-content-end gap-2 pt-4 mt-auto">
                                        <Button variant="link" className="text-700 text-decoration-none px-3" onClick={onHide} disabled={saving || loadingContext}>
                                            Cerrar
                                        </Button>
                                        <Button variant="primary" onClick={handleSave} disabled={saving || loadingContext || loadingBranches || !createUserContext || !selectedBranchId}>
                                            {(saving || loadingContext || loadingBranches) ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    {loadingContext || loadingBranches ? 'Preparando...' : 'Creando...'}
                                                </>
                                            ) : 'Crear Usuario'}
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Modal.Body>
            </Modal>

            <ModalConfirmacionPagoSuscripcion
                show={showModalConfirmacion}
                onHide={() => {
                    setShowModalConfirmacion(false);
                    setPayloadPendiente(null);
                }}
                onConfirm={handleConfirmacionPago}
                diferencia={payloadPendiente?.dCobroDiferencial || createUserContext?.dCobroDiferencial || 0}
                moneda={createUserContext?.vMoneda || infoSuscripcionProp?.vMoneda || 'MXN'}
            />
        </>
    );
};

export default ModalNuevoUsuarioAkasiaPOS;

ModalNuevoUsuarioAkasiaPOS.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    licencias: PropTypes.shape({
        idClienteAkasia: PropTypes.number,
        idLicencia: PropTypes.number,
        vUUIDLicencia: PropTypes.string,
        vUUIDEmpresa: PropTypes.string
    }),
    onCreated: PropTypes.func,
    infoSuscripcionProp: PropTypes.shape({
        idSuscripcion: PropTypes.number,
        iStatus: PropTypes.number,
        vStripeSubscriptionId: PropTypes.string,
        vStripeProductId: PropTypes.string,
        vStripePriceId: PropTypes.string,
        vTipoRenovacion: PropTypes.string,
        idCatFormaPago: PropTypes.number,
        vMoneda: PropTypes.string
    })
};
